import React from "react";
import TrustSmall from "@/components/layouts/TrustSmall";
import { AccreditationSmallSection, Hero } from "@/.storyblok/types/319751/storyblok-components";
import { storyblokEditable, SbBlokData } from "@storyblok/react";
import QuoteFormHeader from "@/components/layouts/QuoteHeader";
import Image from "next/image";
import { renderRichText } from "@/lib/richText";

interface HeroProps {
  blok: Hero & SbBlokData;
  trustSmall?: AccreditationSmallSection & SbBlokData;
}

const HeroComponent: React.FC<HeroProps> = ({ blok, trustSmall }) => {
  if (!blok) return null;

  const { title, subtitle, hero_type, form_title, cta_button, cta_button_link, trust_small_label, image, rich_text, button_label } = blok;

  const href =
    typeof cta_button_link === "object" && cta_button_link.url
      ? cta_button_link.url
      : undefined;

  if (hero_type === "center_align") {
    return (
      <div className="hero">
        <div className="hero-inner">
        {title && (<h1 className="mb-3" style={{ textRendering: 'optimizeSpeed' }}>{title}</h1>)}
          {subtitle ? (
            <p className="lead">{subtitle}</p>
          ): null}
          {rich_text && typeof renderRichText(rich_text) === 'string' ? (
            <div className="hero-subtitle" dangerouslySetInnerHTML={{ __html: renderRichText(rich_text) }} />
          ) : null}
          {cta_button && href && (
            <a
              className="button button-large button-primary js-cta-button"
              href={href}
              target={cta_button_link?.target || "_self"}
            >
              {cta_button}
            </a>
          )}
        </div>
      </div>
    );
  }

  if (hero_type === "center_text_only") {
    return (
      <div {...(blok && storyblokEditable(blok))} className="hero center_text_only">
        <div className="hero-inner">
          <h1 className="my-4" style={{ textRendering: 'optimizeSpeed' }}>{title}&nbsp;</h1>
        </div>
      </div>
    );
  }

  if (hero_type === "with_image") {
    return (
      <div {...(blok && storyblokEditable(blok))} className="hero">
        <div className="hero-inner">
          <div className="row text-start mb-3 mb-lg-0 align-items-center">
            <div className="col-24 col-lg-13 mb-4 mb-lg-0">
              <h1 style={{ textRendering: 'optimizeSpeed' }}>{title}&nbsp;</h1>
              {subtitle ? (
                <p className="hero-subtitle mb-4">{subtitle}</p>
              ): null}
              {rich_text && typeof renderRichText(rich_text) === 'string' ? (
                <div className="hero-subtitle mb-4" dangerouslySetInnerHTML={{ __html: renderRichText(rich_text) }} />
              ) : null}
              {trustSmall && <TrustSmall blok={trustSmall} title={trust_small_label} />}
            </div>
            {image?.filename && (
              <div className="col-24 col-lg-10 offset-lg-1 mt-5 mt-lg-0">
                <Image
                  src={image.filename}
                  alt={image.alt || title || ""}
                  width={image.width || 500}
                  height={image.height || 500}
                  className="img-fluid"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  loading="eager"
                  quality={75}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div {...(blok && storyblokEditable(blok))} className="hero pb-5 pt-0 my-3 my-xl-5">
      <div className="hero-inner">
        <div className="row text-start align-items-center mb-3 mb-lg-0 gy-5 gy-lg-0">
          <div className="col-lg-13 mb-5 mb-lg-0">
            {title && (<h1 className="mb-3" style={{ textRendering: 'optimizeSpeed' }}>{title}</h1>)}
            {subtitle && (<p className="hero-subtitle mb-5">{subtitle}</p>)}
            {rich_text && typeof renderRichText(rich_text) === 'string' ? (
              <div className="hero-subtitle mb-5" dangerouslySetInnerHTML={{ __html: renderRichText(rich_text) }} />
            ) : null}
            {trustSmall && <TrustSmall blok={trustSmall}  title={trust_small_label} />}
          </div>
          <QuoteFormHeader formTitle={form_title} buttonLabel={button_label || "Submit"}></QuoteFormHeader>
        </div>
      </div>
    </div>
  );
};

export default HeroComponent;